import { Box, Card, Text, Group, Badge, Button, Stack, Grid, Modal, LoadingOverlay, NumberInput } from '@mantine/core';
import { IconCalendar, IconAlertCircle, IconHeadset } from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import {
  getCurrentSubscription,
  cancelSubscription,
  reactivateSubscription,
  listConsultingServices,
  updateConsultingItems,
  SubscriptionResponse,
  SubscriptionStatus,
  ScheduledSubscription,
  type ConsultingServiceInfo,
} from '@mygpt/app';
import { showNotification } from '@mantine/notifications';

export const SubscriptionOverview = () => {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [scheduledSubscription, setScheduledSubscription] = useState<ScheduledSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelModalOpened, setCancelModalOpened] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [cancelScheduledModalOpened, setCancelScheduledModalOpened] = useState(false);
  const [cancelingScheduled, setCancelingScheduled] = useState(false);

  // v0.8 多咨询服务调整
  const [consultingCfgs, setConsultingCfgs] = useState<ConsultingServiceInfo[]>([]);
  const [consultingModalOpened, setConsultingModalOpened] = useState(false);
  const [consultingDraftQtys, setConsultingDraftQtys] = useState<Record<string, number>>({});
  const [savingConsulting, setSavingConsulting] = useState(false);

  const format = (time?: string) => {
    if (!time) return '';
    // 强制按 JST 显示订阅日期：dealer 渠道的 trial_end 是「次月 1 日 00:00 JST」，
    // 浏览器若在 UTC 等其他时区会显示前一日（如 5/31 而不是 6/1），统一 'Asia/Tokyo'
    return dayjs(time).tz('Asia/Tokyo').format('YYYY-MM-DD');
  };

  const formatPrice = (amount?: number, currency?: string) => {
    if (amount == null) return '';
    return `¥${amount.toLocaleString()}`;
  };

  const fetchSubscriptionData = useCallback(async () => {
    const res = await getCurrentSubscription();
    setSubscription(res.data?.subscription || null);
    setScheduledSubscription(res.data?.scheduled_subscription || null);
  }, []);

  /** 延迟刷新：等待后端异步处理完成（如 Stripe webhook 回调） */
  const delayedRefresh = useCallback(
    async (delayMs = 1000) => {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      await fetchSubscriptionData();
    },
    [fetchSubscriptionData]
  );

  useEffect(() => {
    const fetchSubscription = async () => {
      setLoading(true);
      try {
        await fetchSubscriptionData();
      } catch {
        // 错误由全局拦截器统一处理
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
    listConsultingServices()
      .then((res) => setConsultingCfgs(res.data || []))
      .catch(() => setConsultingCfgs([]));
  }, []);

  // 打开 Modal 时预填当前订阅已有的 quantity（其他 service 默认 0）
  const openConsultingModal = () => {
    const current: Record<string, number> = {};
    consultingCfgs.forEach((cfg) => {
      const matched = subscription?.consulting_items?.find((it) => it.name === cfg.name);
      current[cfg.id] = matched?.quantity ?? 0;
    });
    setConsultingDraftQtys(current);
    setConsultingModalOpened(true);
  };

  const handleSaveConsulting = async () => {
    setSavingConsulting(true);
    try {
      const items = Object.entries(consultingDraftQtys).map(([service_id, quantity]) => ({
        service_id,
        quantity,
      }));
      await updateConsultingItems({ items });
      showNotification({
        color: 'green',
        message: t('subscription.consulting.update_success'),
      });
      setConsultingModalOpened(false);
    } catch (e: any) {
      showNotification({ color: 'red', message: e?.response?.data?.detail || String(e) });
    } finally {
      setSavingConsulting(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      await cancelSubscription({
        cancel_at_period_end: true,
        subscription_id: subscription!.stripe_subscription_id,
      });

      showNotification({
        color: 'green',
        message: t('subscription.messages.cancelSuccess'),
      });
      setCancelModalOpened(false);
    } catch {
      // 错误由全局拦截器统一处理
      setCanceling(false);
      return;
    }
    // 延迟刷新，等待后端异步处理完成
    try {
      await delayedRefresh();
    } catch {
      // 刷新失败不影响取消结果
    } finally {
      setCanceling(false);
    }
  };

  const handleCancelScheduledSubscription = async () => {
    if (!scheduledSubscription) return;
    setCancelingScheduled(true);
    try {
      await cancelSubscription({ schedule_id: scheduledSubscription.stripe_schedule_id });

      showNotification({
        color: 'green',
        message: t('subscription.scheduled.cancelSuccess'),
      });
      setCancelScheduledModalOpened(false);
    } catch {
      // 错误由全局拦截器统一处理
      setCancelingScheduled(false);
      return;
    }
    // 延迟刷新，等待后端异步处理完成
    try {
      await delayedRefresh();
    } catch {
      // 刷新失败不影响取消结果
    } finally {
      setCancelingScheduled(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      await reactivateSubscription();

      showNotification({
        color: 'green',
        message: t('subscription.messages.reactivateSuccess'),
      });
    } catch {
      // 错误由全局拦截器统一处理
      setReactivating(false);
      return;
    }
    // 延迟刷新，等待后端异步处理完成
    try {
      await delayedRefresh();
    } catch {
      // 刷新失败不影响操作结果
    } finally {
      setReactivating(false);
    }
  };

  /** 获取套餐名称，兼容新旧接口 */
  const getPlanName = (sub: SubscriptionResponse) => {
    return sub.plan?.name || sub.plan_name || '';
  };

  const getStatusBadge = (status: SubscriptionStatus) => {
    const statusConfig = {
      [SubscriptionStatus.ACTIVE]: { color: 'green', label: t('subscription.status.active') },
      [SubscriptionStatus.CANCELED]: { color: 'red', label: t('subscription.status.canceled') },
      [SubscriptionStatus.INCOMPLETE]: { color: 'yellow', label: t('subscription.status.incomplete') },
      [SubscriptionStatus.INCOMPLETE_EXPIRED]: { color: 'gray', label: t('subscription.status.incompleteExpired') },
      [SubscriptionStatus.PAST_DUE]: { color: 'orange', label: t('subscription.status.pastDue') },
      [SubscriptionStatus.TRIALING]: { color: 'blue', label: t('subscription.status.trialing') },
      [SubscriptionStatus.UNPAID]: { color: 'red', label: t('subscription.status.unpaid') },
    };

    const config = statusConfig[status] || statusConfig[SubscriptionStatus.ACTIVE];
    return (
      <Badge color={config.color} variant="filled">
        {config.label}
      </Badge>
    );
  };

  return (
    <Box>
      <Card
        shadow="sm"
        padding={0}
        radius="md"
        withBorder
        pos="relative"
        className="overflow-hidden"
        mih={loading ? 200 : undefined}
      >
        <LoadingOverlay visible={loading} />

        {subscription ? (
          <>
            {/* 头部渐变区域 - 套餐名称 + 状态 */}
            <Box className="bg-gradient-to-br from-blue-50 to-indigo-50 px-6 py-5">
              <Group justify="space-between" align="flex-start">
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500} mb={4}>
                    {t('subscription.title')}
                  </Text>
                  <Text size="xl" fw={700}>
                    {getPlanName(subscription)}
                  </Text>
                </Box>
                <Group gap="xs">
                  {getStatusBadge(subscription.status)}
                  {subscription.cancel_at_period_end && (
                    <Badge color="orange" variant="light" size="sm">
                      {t('subscription.cancelScheduled')}
                    </Badge>
                  )}
                </Group>
              </Group>
            </Box>

            {/* 详情区域 */}
            <Box className="px-6 py-5">
              <Grid gutter="xl">
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" mb={4}>
                    {t('subscription.nextBilling')}
                  </Text>
                  <Group gap={6}>
                    <IconCalendar size={14} className="text-slate-400" />
                    <Text size="sm" fw={500}>
                      {format(subscription.current_period_end)}
                    </Text>
                  </Group>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" mb={4}>
                    {t('subscription.period')}
                  </Text>
                  <Group gap={6}>
                    <IconCalendar size={14} className="text-slate-400" />
                    <Text size="sm" fw={500}>
                      {format(subscription.current_period_start)} ~ {format(subscription.current_period_end)}
                    </Text>
                  </Group>
                </Grid.Col>
              </Grid>

              {subscription.consulting_items && subscription.consulting_items.length > 0 && (
                <Box className="mt-4 rounded-md bg-indigo-50 px-3 py-2">
                  <Text size="xs" c="dimmed" mb={4}>
                    {t('subscription.consulting.items_title')}
                  </Text>
                  <Stack gap={4}>
                    {subscription.consulting_items.map((it, i) => (
                      <Text key={i} size="sm">
                        {it.name} × {it.quantity}
                        <Text component="span" size="xs" c="dimmed">
                          {' '}
                          (¥{it.unit_amount.toLocaleString('ja-JP')} / 回)
                        </Text>
                      </Text>
                    ))}
                  </Stack>
                </Box>
              )}

              {subscription.canceled_at && (
                <Box className="mt-4 rounded-md bg-red-50 px-3 py-2">
                  <Text size="xs" c="red.7">
                    {t('subscription.canceledAt')}: {format(subscription.canceled_at)}
                  </Text>
                </Box>
              )}

              {/* 操作按钮 */}
              <Group mt="lg">
                {subscription.cancel_at_period_end ? (
                  <Button
                    variant="light"
                    color="green"
                    size="xs"
                    onClick={handleReactivateSubscription}
                    loading={reactivating}
                  >
                    {t('subscription.actions.reactivate')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    color="gray"
                    size="xs"
                    onClick={() => setCancelModalOpened(true)}
                    disabled={
                      subscription.status !== SubscriptionStatus.ACTIVE &&
                      subscription.status !== SubscriptionStatus.TRIALING
                    }
                  >
                    {t('subscription.actions.cancel')}
                  </Button>
                )}
                {consultingCfgs.length > 0 && (
                  <Button
                    variant="light"
                    color="indigo"
                    size="xs"
                    leftSection={<IconHeadset size={14} />}
                    onClick={openConsultingModal}
                  >
                    {t('subscription.consulting.adjust')}
                  </Button>
                )}
              </Group>
            </Box>
          </>
        ) : (
          !loading && (
            <Text c="dimmed" ta="center" py="xl">
              {t('subscription.empty')}
            </Text>
          )
        )}
      </Card>

      {/* 下期订阅 */}
      {scheduledSubscription && (
        <Card shadow="sm" padding={0} radius="md" withBorder mt="md" className="overflow-hidden">
          {/* 头部渐变区域 */}
          <Box className="bg-gradient-to-br from-violet-50 to-purple-50 px-6 py-5">
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500} mb={4}>
                  {t('subscription.scheduled.title')}
                </Text>
                <Text size="xl" fw={700}>
                  {scheduledSubscription.plan.name}
                </Text>
              </Box>
              <Badge color="violet" variant="light" size="sm">
                {t('subscription.scheduled.pending')}
              </Badge>
            </Group>
          </Box>

          {/* 详情区域 */}
          <Box className="px-6 py-5">
            <Grid gutter="xl">
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed" mb={4}>
                  {t('subscription.scheduled.startDate')}
                </Text>
                <Group gap={6}>
                  <IconCalendar size={14} className="text-slate-400" />
                  <Text size="sm" fw={500}>
                    {format(scheduledSubscription.scheduled_start_date)}
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed" mb={4}>
                  {t('subscription.scheduled.price')}
                </Text>
                <Group gap={6}>
                  <IconCalendar size={14} className="text-slate-400" />
                  <Text size="sm" fw={500}>
                    {scheduledSubscription.price
                      ? `${formatPrice(scheduledSubscription.price.unit_amount)}/${
                          scheduledSubscription.price.billing_interval === 'month'
                            ? t('subscription.scheduled.month')
                            : t('subscription.scheduled.year')
                        }`
                      : '-'}
                  </Text>
                </Group>
              </Grid.Col>
            </Grid>

            <Group mt="lg">
              <Button variant="outline" color="gray" size="xs" onClick={() => setCancelScheduledModalOpened(true)}>
                {t('subscription.scheduled.cancel')}
              </Button>
            </Group>
          </Box>
        </Card>
      )}

      <Modal
        opened={cancelModalOpened}
        onClose={() => setCancelModalOpened(false)}
        title={t('subscription.cancelModal.title')}
        centered
      >
        <Stack gap="md">
          <Group>
            <IconAlertCircle size={24} color="orange" />
            <Text fw={500}>{t('subscription.cancelModal.confirm')}</Text>
          </Group>

          <Text size="sm" c="dimmed">
            {t('subscription.cancelModal.description', {
              date: subscription && format(subscription.current_period_end),
            })}
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={() => setCancelModalOpened(false)}>
              {t('subscription.cancelModal.back')}
            </Button>
            <Button color="red" onClick={handleCancelSubscription} loading={canceling}>
              {t('subscription.cancelModal.confirmButton')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={cancelScheduledModalOpened}
        onClose={() => setCancelScheduledModalOpened(false)}
        title={t('subscription.scheduled.cancel')}
        centered
      >
        <Stack gap="md">
          <Group>
            <IconAlertCircle size={24} color="orange" />
            <Text fw={500}>{t('subscription.scheduled.cancelConfirm')}</Text>
          </Group>

          <Text size="sm" c="dimmed">
            {t('subscription.scheduled.cancelDescription')}
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={() => setCancelScheduledModalOpened(false)}>
              {t('subscription.cancelModal.back')}
            </Button>
            <Button color="red" onClick={handleCancelScheduledSubscription} loading={cancelingScheduled}>
              {t('subscription.cancelModal.confirmButton')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* v0.8 多咨询服务数量调整（下个 cycle 生效） */}
      <Modal
        opened={consultingModalOpened}
        onClose={() => setConsultingModalOpened(false)}
        title={t('subscription.consulting.modalTitle')}
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('subscription.consulting.effectiveNote')}
          </Text>
          {consultingCfgs.map((cfg) => (
            <NumberInput
              key={cfg.id}
              label={
                <span>
                  {cfg.name}
                  <Text component="span" size="xs" c="dimmed" ml={6}>
                    （¥{cfg.unit_amount.toLocaleString('ja-JP')} / 回）
                  </Text>
                </span>
              }
              min={0}
              max={cfg.max_quantity_per_subscription}
              value={consultingDraftQtys[cfg.id] ?? 0}
              onChange={(v) =>
                setConsultingDraftQtys((prev) => ({
                  ...prev,
                  [cfg.id]: typeof v === 'number' ? v : 0,
                }))
              }
            />
          ))}
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={() => setConsultingModalOpened(false)}>
              {t('subscription.cancelModal.back')}
            </Button>
            <Button onClick={handleSaveConsulting} loading={savingConsulting}>
              {t('subscription.consulting.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};
